/**
 * Invite List Component
 * Displays user's invite codes with copy functionality
 */
import React, { useState } from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Code,
  Text,
  HStack,
  IconButton,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { Copy, CheckCircle, ExternalLink } from 'lucide-react';
import { type Invite } from '../../services/InviteService';
import { format } from 'date-fns';

interface InviteListProps {
  invites: Invite[];
}

export const InviteList: React.FC<InviteListProps> = ({ invites }) => {
  const toast = useToast();
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  // Format date
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return 'N/A';
    }
  };

  // Get status badge
  const getStatusBadge = (invite: Invite) => {
    if (!invite.is_active) {
      return <Badge colorScheme="gray">Inactive</Badge>;
    }
    if (invite.used_at) {
      return <Badge colorScheme="green">Used</Badge>;
    }
    return <Badge colorScheme="blue">Available</Badge>;
  };

  // Copy invite code
  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
      toast({
        title: 'Copied',
        description: 'Invite code copied to clipboard',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy code',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Copy invite URL
  const handleCopyUrl = async (code: string) => {
    const url = `${window.location.origin}/register?invite=${code}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(code);
      setTimeout(() => setCopiedUrl(null), 2000);
      toast({
        title: 'Copied',
        description: 'Invite URL copied to clipboard',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy URL',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Table variant="simple">
      <Thead bg="gray.50">
        <Tr>
          <Th>Code</Th>
          <Th>Status</Th>
          <Th>Created</Th>
          <Th>Used By</Th>
          <Th>Used At</Th>
          <Th>Actions</Th>
        </Tr>
      </Thead>
      <Tbody>
        {invites.map((invite) => (
          <Tr key={invite.id} _hover={{ bg: 'gray.50' }}>
            {/* Code */}
            <Td>
              <HStack spacing={2}>
                <Code fontSize="sm" colorScheme="blue">
                  {invite.code}
                </Code>
                <Tooltip label={copiedCode === invite.code ? 'Copied!' : 'Copy code'}>
                  <IconButton
                    aria-label="Copy code"
                    icon={copiedCode === invite.code ? <CheckCircle size={14} /> : <Copy size={14} />}
                    size="xs"
                    variant="ghost"
                    onClick={() => handleCopyCode(invite.code)}
                    colorScheme={copiedCode === invite.code ? 'green' : 'gray'}
                  />
                </Tooltip>
              </HStack>
            </Td>

            {/* Status */}
            <Td>{getStatusBadge(invite)}</Td>

            {/* Created */}
            <Td>
              <Text fontSize="sm" color="gray.600">
                {formatDate(invite.created_at)}
              </Text>
            </Td>

            {/* Used By */}
            <Td>
              {invite.used_by ? (
                <Text fontSize="sm" fontWeight="medium">
                  {invite.used_by.username}
                </Text>
              ) : (
                <Text fontSize="sm" color="gray.400">
                  —
                </Text>
              )}
            </Td>

            {/* Used At */}
            <Td>
              <Text fontSize="sm" color="gray.600">
                {invite.used_at ? formatDate(invite.used_at) : '—'}
              </Text>
            </Td>

            {/* Actions */}
            <Td>
              {!invite.used_at && invite.is_active && (
                <Tooltip label={copiedUrl === invite.code ? 'Copied!' : 'Copy invite URL'}>
                  <IconButton
                    aria-label="Copy URL"
                    icon={copiedUrl === invite.code ? <CheckCircle size={16} /> : <ExternalLink size={16} />}
                    size="sm"
                    variant="ghost"
                    onClick={() => handleCopyUrl(invite.code)}
                    colorScheme={copiedUrl === invite.code ? 'green' : 'blue'}
                  />
                </Tooltip>
              )}
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
};

export default InviteList;
